import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Group,
  Loader,
  Center,
  Table,
  Pagination,
  Button,
  Badge,
  TextInput,
  LoadingOverlay,
  Box,
  Tooltip,
  ScrollArea,
  Tabs,
  Modal,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconRefresh,
  IconFilter,
  IconPlus,
  IconLock,
  IconBarcode,
  IconFileAlert,
  IconTrash,
  IconPlayerPlay,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import {
  useCases,
  useCaseStats,
  useMissedCases,
  useDiscardDraftCase,
} from "../../hooks/useCases";
import {
  createCaseListNavigationParams,
  type CaseFilters,
  casesService,
} from "../../services";
import { useTaskStore } from "../../store/task.store";
import { isRouteAllowed } from "../../data/taskNavigation";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import {
  FilterModal,
  ActiveFilters,
  CaseStatusBadge,
} from "../../components/cases";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";

const CASE_CREATED_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const CASE_CREATED_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

export function formatCaseDateOnly(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

export function formatCaseCreatedInstant(value: string): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return "—";
  return `${CASE_CREATED_DATE_FORMATTER.format(
    instant
  )} ${CASE_CREATED_TIME_FORMATTER.format(instant)}`;
}

const ALL_FILTER_KEYS = [
  "search",
  "facilityIds",
  "clientId",
  "sendingPracticeId",
  "physicianId",
  "submittingLabId",
  "surgeryCenterLabId",
  "pathologistIds",
  "currentStatusIds",
  "workflowIds",
  "procedureIds",
  "patientIds",
  "dateFrom",
  "dateTo",
  "dateCollected",
  "isBlocked",
  "isRush",
  "isHighPriority",
  "flagIds",
  "caseType",
  "tatOverdue",
  "tatMinDays",
  "hasPendingTests",
  "currentStep",
  "eligibleForSendOut",
  "sortBy",
  "sortDirection",
  "page",
];

function filtersToParams(f: CaseFilters): Record<string, string | null> {
  return {
    search: f.search || null,
    facilityIds: f.facilityIds?.length ? f.facilityIds.join(",") : null,
    clientId: f.clientId || null,
    sendingPracticeId: f.sendingPracticeId?.length
      ? f.sendingPracticeId.join(",")
      : null,
    physicianId: f.physicianId || null,
    submittingLabId: f.submittingLabId?.length
      ? f.submittingLabId.join(",")
      : null,
    surgeryCenterLabId: f.surgeryCenterLabId?.length
      ? f.surgeryCenterLabId.join(",")
      : null,
    pathologistIds: f.pathologistIds?.length
      ? f.pathologistIds.join(",")
      : null,
    currentStatusIds: f.currentStatusIds?.length
      ? f.currentStatusIds.join(",")
      : null,
    workflowIds: f.workflowIds?.length ? f.workflowIds.join(",") : null,
    procedureIds: f.procedureIds?.length ? f.procedureIds.join(",") : null,
    patientIds: f.patientIds?.length ? f.patientIds.join(",") : null,
    dateFrom: f.dateFrom || null,
    dateTo: f.dateTo || null,
    dateCollected: f.dateCollected || null,
    isBlocked: f.isBlocked ? "1" : null,
    isRush: f.isRush ? "1" : null,
    isHighPriority: f.isHighPriority ? "1" : null,
    flagIds: f.flagDefinitionIds?.length ? f.flagDefinitionIds.join(",") : null,
    caseType: f.caseType || null,
    tatOverdue: f.tatOverdue ? "1" : null,
    tatMinDays: f.tatMinDays != null ? String(f.tatMinDays) : null,
    hasPendingTests: f.hasPendingTests ? "1" : null,
    currentStep: f.currentStep || null,
    eligibleForSendOut: f.eligibleForSendOut ? "1" : null,
    sortBy: f.sortBy || null,
    sortDirection: f.sortDirection || null,
  };
}

export default function CasesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "cases";

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleTabChange = (tab: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab && tab !== "cases") next.set("tab", tab);
        else next.delete("tab");
        next.delete("page");
        next.delete("mpage");
        return next;
      },
      { replace: true }
    );
  };

  const { selectedTasks } = useTaskStore();
  const selectedSlugs = selectedTasks.map((t) => t.slug);
  const canSeeAccessioning = isRouteAllowed("/accessioning", selectedSlugs);

  useEffect(() => {
    if (activeTab === "missed" && !canSeeAccessioning) {
      setSearchParams({}, { replace: false });
    }
  }, [activeTab, canSeeAccessioning, setSearchParams]);

  const pageSize = 20;
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const missedPage = Math.max(
    1,
    parseInt(searchParams.get("mpage") ?? "1", 10) || 1
  );
  const filters = useMemo<CaseFilters>(
    () => ({
      search: searchParams.get("search") || undefined,
      facilityIds:
        searchParams.get("facilityIds")?.split(",").filter(Boolean) ||
        undefined,
      clientId: searchParams.get("clientId") || undefined,
      sendingPracticeId:
        searchParams.get("sendingPracticeId")?.split(",").filter(Boolean) ||
        undefined,
      physicianId: searchParams.get("physicianId") || undefined,
      submittingLabId:
        searchParams.get("submittingLabId")?.split(",").filter(Boolean) ||
        undefined,
      surgeryCenterLabId:
        searchParams.get("surgeryCenterLabId")?.split(",").filter(Boolean) ||
        undefined,
      pathologistIds:
        searchParams.get("pathologistIds")?.split(",").filter(Boolean) ||
        undefined,
      currentStatusIds:
        searchParams.get("currentStatusIds")?.split(",").filter(Boolean) ||
        undefined,
      workflowIds:
        searchParams.get("workflowIds")?.split(",").filter(Boolean) ||
        undefined,
      procedureIds:
        searchParams.get("procedureIds")?.split(",").filter(Boolean) ||
        undefined,
      patientIds:
        searchParams.get("patientIds")?.split(",").filter(Boolean) || undefined,
      dateFrom: searchParams.get("dateFrom") || undefined,
      dateTo: searchParams.get("dateTo") || undefined,
      dateCollected: searchParams.get("dateCollected") || undefined,
      isBlocked: searchParams.get("isBlocked") === "1" ? true : undefined,
      isRush: searchParams.get("isRush") === "1" ? true : undefined,
      isHighPriority:
        searchParams.get("isHighPriority") === "1" ? true : undefined,
      flagDefinitionIds:
        searchParams.get("flagIds")?.split(",").filter(Boolean) || undefined,
      caseType: searchParams.get("caseType") || undefined,
      tatOverdue: searchParams.get("tatOverdue") === "1" ? true : undefined,
      tatMinDays: searchParams.get("tatMinDays")
        ? parseInt(searchParams.get("tatMinDays")!, 10)
        : undefined,
      hasPendingTests:
        searchParams.get("hasPendingTests") === "1" ? true : undefined,
      currentStep: searchParams.get("currentStep") || undefined,
      eligibleForSendOut:
        searchParams.get("eligibleForSendOut") === "1" ? true : undefined,
      sortBy:
        searchParams.get("sortBy") === "caseNumber" ? "caseNumber" : undefined,
      sortDirection:
        searchParams.get("sortDirection") === "asc"
          ? "asc"
          : searchParams.get("sortDirection") === "desc"
          ? "desc"
          : undefined,
    }),
    [searchParams]
  );
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const [discardingCaseId, setDiscardingCaseId] = useState<string | null>(null);
  const [discardCaseNumber, setDiscardCaseNumber] = useState<string>("");

  const {
    data: casesData,
    isLoading: casesLoading,
    isFetching: casesFetching,
    refetch: refetchCases,
  } = useCases({ ...filters, search: filters.search }, page, pageSize);

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
  } = useCaseStats(filters);

  const {
    data: missedCasesData,
    isLoading: missedLoading,
    isFetching: missedFetching,
    refetch: refetchMissed,
  } = useMissedCases(missedPage, pageSize, { enabled: canSeeAccessioning });

  const discardMutation = useDiscardDraftCase();

  const handleFiltersApply = useCallback(
    (newFilters: CaseFilters) => {
      updateParams({ ...filtersToParams(newFilters), page: null });
    },
    [updateParams]
  );

  const handleFiltersChange = useCallback(
    (newFilters: CaseFilters) => {
      updateParams({ ...filtersToParams(newFilters), page: null });
    },
    [updateParams]
  );

  const handleClearAllFilters = useCallback(() => {
    updateParams(Object.fromEntries(ALL_FILTER_KEYS.map((k) => [k, null])));
  }, [updateParams]);

  const handleBarcodeSubmit = async () => {
    if (!barcodeInput.trim()) {
      return;
    }

    setBarcodeError(null);
    setBarcodeLoading(true);

    try {
      const caseNumber = barcodeInput.trim().split(";")[0];
      const caseData = await casesService.getByCaseNumber(caseNumber);

      if (caseData) {
        navigate(`/cases/${caseData.id}`);
      } else {
        setBarcodeError(`Case not found: ${caseNumber}`);
        setBarcodeInput("");
      }
    } catch (err: any) {
      setBarcodeError(err.message || "Failed to find case");
      setBarcodeInput("");
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBarcodeSubmit();
    }
  };

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        setBarcodeError(null);
        setBarcodeLoading(true);
        setBarcodeInput(barcode);
        try {
          const caseNumber = barcode.split(";")[0];
          const caseData = await casesService.getByCaseNumber(caseNumber);
          if (caseData) {
            navigate(`/cases/${caseData.id}`);
          } else {
            setBarcodeError(`Case not found: ${caseNumber}`);
            setBarcodeInput("");
          }
        } catch (err: any) {
          setBarcodeError(err.message || "Failed to find case");
          setBarcodeInput("");
        } finally {
          setBarcodeLoading(false);
        }
      },
      [navigate]
    )
  );

  const handleViewCase = useCallback(
    (caseId: string) => {
      const context = createCaseListNavigationParams(filters);
      navigate(`/cases/${caseId}?${context.toString()}`);
    },
    [filters, navigate]
  );

  const handleSort = useCallback(
    (field: "caseNumber") => {
      const isAsc = filters.sortBy === field && filters.sortDirection === "asc";
      updateParams({
        sortBy: field,
        sortDirection: isAsc ? "desc" : "asc",
      });
    },
    [filters.sortBy, filters.sortDirection, updateParams]
  );

  const handleCaseNumberSort = useCallback(
    () => handleSort("caseNumber"),
    [handleSort]
  );

  const handleDiscardDraft = (caseId: string, caseNumber: string) => {
    setDiscardingCaseId(caseId);
    setDiscardCaseNumber(caseNumber);
  };

  const confirmDiscard = async () => {
    if (!discardingCaseId) return;
    await discardMutation.mutateAsync(discardingCaseId);
    setDiscardingCaseId(null);
    setDiscardCaseNumber("");
  };

  const cases = casesData?.data || [];
  const total = casesData?.pagination?.total || 0;
  const totalPages = casesData?.pagination?.totalPages || 1;

  const missedCases = missedCasesData?.data || [];
  const missedTotal = missedCasesData?.pagination?.total || 0;
  const missedTotalPages = missedCasesData?.pagination?.totalPages || 1;

  const hasActiveFilters = Object.keys(filters).some((key) => {
    const value = filters[key as keyof CaseFilters];
    if (value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    return true;
  });

  if (casesLoading && !casesData) {
    return (
      <Container fluid py="xl">
        <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
          <Center h={400}>
            <Loader size="lg" />
          </Center>
        </Box>
      </Container>
    );
  }

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header Row */}
          <Group justify="space-between">
            <Group gap="md" align="center">
              <Title order={2}>Cases</Title>
              {stats && !statsLoading && (
                <Group gap="xs">
                  <Badge size="lg" variant="light" color="blue">
                    Total: {stats.total}
                  </Badge>
                  {stats.blocked > 0 && (
                    <Badge size="lg" variant="light" color="red">
                      <Group gap={4}>
                        <IconAlertTriangle size={14} />
                        Blocked: {stats.blocked}
                      </Group>
                    </Badge>
                  )}
                  <Badge size="lg" variant="light" color="grape">
                    Workflows: {stats.byWorkflow.length}
                  </Badge>
                </Group>
              )}
            </Group>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate("/accessioning")}
            >
              New Case
            </Button>
          </Group>

          {/* Tabs: Cases / Missed Cases */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="cases">Cases</Tabs.Tab>
              {canSeeAccessioning && (
                <Tabs.Tab
                  value="missed"
                  leftSection={<IconFileAlert size={16} />}
                  rightSection={
                    missedTotal > 0 ? (
                      <Badge size="sm" variant="filled" color="orange">
                        {missedTotal}
                      </Badge>
                    ) : undefined
                  }
                >
                  Missed Cases
                </Tabs.Tab>
              )}
            </Tabs.List>

            {/* ===== CASES TAB ===== */}
            <Tabs.Panel value="cases" pt="md">
              <Stack gap="lg">
                {/* Barcode Scanner and Filters Row */}
                <Group align="stretch" grow>
                  <Paper p="md" withBorder style={{ flex: 2 }}>
                    <Stack gap="sm">
                      <Group>
                        <IconBarcode size={24} />
                        <Text fw={500}>Scan Case Barcode</Text>
                      </Group>
                      <Group>
                        <TextInput
                          placeholder="Scan or enter case # (e.g., G25-00001)"
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          onKeyPress={handleBarcodeKeyPress}
                          style={{ flex: 1 }}
                          autoFocus
                          error={barcodeError}
                        />
                        <Button
                          onClick={handleBarcodeSubmit}
                          loading={barcodeLoading}
                          disabled={!barcodeInput.trim()}
                        >
                          Open Case
                        </Button>
                      </Group>
                      <Text size="sm" c="dimmed">
                        Scan a barcode to quickly open the case details
                      </Text>
                    </Stack>
                  </Paper>

                  <Paper p="md" withBorder style={{ flex: 1 }}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group>
                          <IconFilter size={24} />
                          <Text fw={500}>Case Filters</Text>
                        </Group>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => refetchCases()}
                          loading={casesFetching || statsFetching}
                          p={4}
                        >
                          <IconRefresh
                            size={16}
                            style={{
                              animation:
                                casesFetching || statsFetching
                                  ? "spin 1s linear infinite"
                                  : "none",
                            }}
                          />
                        </Button>
                      </Group>
                      <Button
                        variant="light"
                        onClick={openFilterModal}
                        fullWidth
                      >
                        {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                      </Button>
                      {hasActiveFilters && (
                        <ActiveFilters
                          filters={filters}
                          onFiltersChange={handleFiltersChange}
                          onClearAll={handleClearAllFilters}
                          showRelationshipFilters
                        />
                      )}
                      <Text size="sm" c="dimmed">
                        Showing {cases.length} of {total} cases
                      </Text>
                    </Stack>
                  </Paper>
                </Group>

                {/* Results Info + Pagination */}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Showing {cases.length} of {total} cases
                  </Text>
                  {totalPages > 1 && (
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={(p) =>
                        updateParams({ page: p === 1 ? null : String(p) })
                      }
                      size="sm"
                    />
                  )}
                </Group>

                {/* Cases Table */}
                <Paper p="md" withBorder pos="relative">
                  <LoadingOverlay
                    visible={casesLoading}
                    overlayProps={{ blur: 1 }}
                    loaderProps={{ size: "md" }}
                  />

                  {cases.length === 0 && !casesLoading ? (
                    <Center py={60}>
                      <Stack align="center" gap="xs">
                        <Text c="dimmed">
                          {filters.search || hasActiveFilters
                            ? "No cases match the current filters"
                            : "No cases found"}
                        </Text>
                        {(filters.search || hasActiveFilters) && (
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={handleClearAllFilters}
                          >
                            Clear all filters
                          </Button>
                        )}
                      </Stack>
                    </Center>
                  ) : (
                    <ScrollArea h="calc(100vh - 430px)" type="auto">
                      <Table
                        striped
                        highlightOnHover
                        stickyHeader
                        stickyHeaderOffset={0}
                      >
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th
                              aria-sort={
                                filters.sortBy === "caseNumber"
                                  ? filters.sortDirection === "asc"
                                    ? "ascending"
                                    : "descending"
                                  : "none"
                              }
                            >
                              <UnstyledButton
                                onClick={handleCaseNumberSort}
                                aria-label={`Sort by Case Number ${
                                  filters.sortBy === "caseNumber"
                                    ? filters.sortDirection === "asc"
                                      ? "descending"
                                      : "ascending"
                                    : "ascending"
                                }`}
                                style={{ width: "100%" }}
                              >
                                <Group gap={4}>
                                  <Text fw={700}>Case</Text>
                                  {filters.sortBy === "caseNumber" &&
                                    (filters.sortDirection === "asc" ? (
                                      <IconChevronUp
                                        size={14}
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <IconChevronDown
                                        size={14}
                                        aria-hidden="true"
                                      />
                                    ))}
                                </Group>
                              </UnstyledButton>
                            </Table.Th>
                            <Table.Th>Provider</Table.Th>
                            <Table.Th>Patient</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Workflow</Table.Th>
                            <Table.Th>Pathologist</Table.Th>
                            <Table.Th>Specimens</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {cases.map((caseItem) => (
                            <Table.Tr
                              key={caseItem.id}
                              style={{
                                cursor: "pointer",
                                opacity: caseItem.isBlocked ? 0.8 : 1,
                              }}
                              onClick={() => handleViewCase(caseItem.id)}
                            >
                              <Table.Td>
                                <Stack gap={2}>
                                  {caseItem.caseNumber && (
                                    <Text size="sm" fw={500}>
                                      {caseItem.caseNumber}
                                    </Text>
                                  )}
                                  {caseItem.externalCaseNumber && (
                                    <Badge
                                      size="xs"
                                      variant="outline"
                                      color="gray"
                                    >
                                      Ext: {caseItem.externalCaseNumber}
                                    </Badge>
                                  )}
                                  <Text size="xs" c="dimmed">
                                    {formatCaseDateOnly(caseItem.dateReceived)}
                                  </Text>
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                <Stack gap={0}>
                                  {caseItem.physician && (
                                    <Text size="sm">
                                      {caseItem.physician.firstName}{" "}
                                      {caseItem.physician.lastName}
                                    </Text>
                                  )}
                                  {caseItem.surgeryCenterLab && (
                                    <Text size="xs" c="dimmed">
                                      {caseItem.surgeryCenterLab.name}
                                    </Text>
                                  )}
                                  {caseItem.sendingPractice && (
                                    <Text size="xs" c="dimmed">
                                      {caseItem.sendingPractice.name}
                                    </Text>
                                  )}
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                {caseItem.patient && (
                                  <Stack gap={0}>
                                    <Text size="sm">
                                      {caseItem.patient.lastName},{" "}
                                      {caseItem.patient.firstName}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      DOB:{" "}
                                      {formatCaseDateOnly(
                                        caseItem.patient.dateOfBirth
                                      )}
                                    </Text>
                                    {caseItem.patient.medicalRecordNumber && (
                                      <Text size="xs" c="dimmed">
                                        MRN:{" "}
                                        {caseItem.patient.medicalRecordNumber}
                                      </Text>
                                    )}
                                  </Stack>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Stack gap={4}>
                                  <Group gap={4} wrap="nowrap">
                                    {caseItem.lockedBy && (
                                      <IconLock size={14} color="orange" />
                                    )}
                                    <CaseStatusBadge
                                      statusName={
                                        caseItem.currentStatus?.name ||
                                        "Unknown"
                                      }
                                      isBlocked={caseItem.isBlocked}
                                    />
                                  </Group>
                                  {(caseItem.isRush ||
                                    caseItem.isHighPriority) && (
                                    <Group gap={4} wrap="nowrap">
                                      {caseItem.isRush && (
                                        <Tooltip
                                          label={
                                            caseItem.rushReason
                                              ? `Rush: ${caseItem.rushReason}`
                                              : "Rush"
                                          }
                                          multiline
                                          maw={300}
                                        >
                                          <Badge
                                            color="red"
                                            variant="filled"
                                            size="xs"
                                          >
                                            Rush
                                          </Badge>
                                        </Tooltip>
                                      )}
                                      {caseItem.isHighPriority && (
                                        <Tooltip
                                          label={
                                            caseItem.highPriorityReason
                                              ? `High Priority: ${caseItem.highPriorityReason}`
                                              : "High Priority"
                                          }
                                          multiline
                                          maw={300}
                                        >
                                          <Badge
                                            color="orange"
                                            variant="filled"
                                            size="xs"
                                          >
                                            High Priority
                                          </Badge>
                                        </Tooltip>
                                      )}
                                    </Group>
                                  )}
                                  {caseItem.isBlocked && (
                                    <Badge
                                      color="red"
                                      variant="light"
                                      size="xs"
                                    >
                                      Blocked
                                    </Badge>
                                  )}
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                {caseItem.workflow && (
                                  <Text size="sm">
                                    {caseItem.workflow.name ||
                                      caseItem.workflow.accessioningType
                                        ?.replace(/_/g, " ")
                                        .replace(/\b\w/g, (c: string) =>
                                          c.toUpperCase()
                                        )}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {caseItem.pathologist && (
                                  <Text size="sm">
                                    {caseItem.pathologist.fullName}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Stack gap={0}>
                                  <Text size="sm">
                                    {caseItem.specimenCount || 0} specimens
                                  </Text>
                                  <Text size="sm" c="dimmed">
                                    {caseItem.blockCount || 0} blocks
                                  </Text>
                                </Stack>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Paper>
              </Stack>
            </Tabs.Panel>

            {/* ===== MISSED CASES TAB ===== */}
            {canSeeAccessioning && (
              <Tabs.Panel value="missed" pt="md">
                <Stack gap="lg">
                  <Group justify="space-between">
                    <Stack gap={4}>
                      <Text size="sm" c="dimmed">
                        Draft cases with no patient, specimen, or procedure
                        data. These are typically created when accessioning is
                        opened accidentally.
                      </Text>
                      <Text size="sm" c="dimmed">
                        Stale missed cases older than 1 week are automatically
                        cleaned up every Sunday.
                      </Text>
                    </Stack>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={() => refetchMissed()}
                      loading={missedFetching}
                      p={4}
                    >
                      <IconRefresh
                        size={16}
                        style={{
                          animation: missedFetching
                            ? "spin 1s linear infinite"
                            : "none",
                        }}
                      />
                    </Button>
                  </Group>

                  {/* Missed Cases Pagination */}
                  {missedTotalPages > 1 && (
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Showing {missedCases.length} of {missedTotal} missed
                        cases
                      </Text>
                      <Pagination
                        total={missedTotalPages}
                        value={missedPage}
                        onChange={(p) =>
                          updateParams({ mpage: p === 1 ? null : String(p) })
                        }
                        size="sm"
                      />
                    </Group>
                  )}

                  <Paper p="md" withBorder pos="relative">
                    <LoadingOverlay
                      visible={missedLoading}
                      overlayProps={{ blur: 1 }}
                      loaderProps={{ size: "md" }}
                    />

                    {missedCases.length === 0 && !missedLoading ? (
                      <Center py={60}>
                        <Stack align="center" gap="xs">
                          <IconFileAlert size={48} color="gray" opacity={0.4} />
                          <Text c="dimmed">No missed cases found</Text>
                          <Text size="sm" c="dimmed">
                            All draft cases have data or have been cleaned up
                          </Text>
                        </Stack>
                      </Center>
                    ) : (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Case #</Table.Th>
                            <Table.Th>Date Created</Table.Th>
                            <Table.Th>Created By</Table.Th>
                            <Table.Th>Facility</Table.Th>
                            <Table.Th>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {missedCases.map((caseItem) => (
                            <Table.Tr key={caseItem.id}>
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {caseItem.caseNumber || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {formatCaseCreatedInstant(caseItem.createdAt)}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {(caseItem as any).createdBy?.fullName || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {caseItem.facility?.name || "—"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs">
                                  <Tooltip label="Resume accessioning this case">
                                    <Button
                                      size="xs"
                                      variant="light"
                                      color="blue"
                                      leftSection={<IconPlayerPlay size={14} />}
                                      onClick={() =>
                                        navigate(`/accessioning/${caseItem.id}`)
                                      }
                                    >
                                      Resume
                                    </Button>
                                  </Tooltip>
                                  <Tooltip label="Permanently delete this empty draft">
                                    <Button
                                      size="xs"
                                      variant="light"
                                      color="red"
                                      leftSection={<IconTrash size={14} />}
                                      onClick={() =>
                                        handleDiscardDraft(
                                          caseItem.id,
                                          caseItem.caseNumber
                                        )
                                      }
                                      loading={
                                        discardMutation.isPending &&
                                        discardingCaseId === caseItem.id
                                      }
                                    >
                                      Discard
                                    </Button>
                                  </Tooltip>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Paper>
                </Stack>
              </Tabs.Panel>
            )}
          </Tabs>
        </Stack>

        {/* Filter Modal */}
        <FilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          allowedStatusNames={["*"]}
          showRelationshipFilters
        />

        {/* Discard Confirmation Modal */}
        <Modal
          opened={!!discardingCaseId}
          onClose={() => setDiscardingCaseId(null)}
          title="Discard Case"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm">
              Discard case <strong>{discardCaseNumber}</strong>? This cannot be
              undone.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => setDiscardingCaseId(null)}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDiscard}
                loading={discardMutation.isPending}
              >
                Discard Case
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
